import { ReactNode } from 'react';
import { useCommands, useLogger, useRedirectToMainApp, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { NameStepData, PhoneNumberStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { useLocation } from '@packages/shared/core/location/hooks';
import { useExperts } from '@packages/shared/core/experts/hooks';
import { useChat } from '@packages/shared/core/chat/hooks';
import { stringifyQueryParams } from '@packages/shared/core/common/utils/query';

type PhoneNumberWithRedirectQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const PhoneNumberWithRedirectQuizStep = ({ id, renderQuizScreen }: PhoneNumberWithRedirectQuizStepProps) => {
  const logger = useLogger();
  const { userService, analyticsService, chatService } = useServices();

  const { stepData, totalSteps, stepOrder, updateUserPreferences, flowName } = useQuizStep(id);
  const { dataKey } = stepData as NameStepData;

  const { country: userCountry } = useLocation();
  const { selectedExpert } = useExperts({ flowName });
  const { redirectToExpertsPage } = useRedirectToMainApp();
  const { createCommand } = useCommands();

  const expertId = selectedExpert ? selectedExpert.id : '';
  const { outgoingMessage } = useChat(expertId);

  if (!stepData) {
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const onSubmit = async (phoneNumber: string) => {
    analyticsService.smsInputScreenClicked({ eventValue: 'opted_in' });

    await updatePhoneNumber(phoneNumber);
    await redirectToChat();
  };

  const updatePhoneNumber = async (phoneNumber: string) => {
    try {
      await userService.updateUserInformation({ phone_number: phoneNumber });
      updateUserPreferences(dataKey, phoneNumber);
    } catch (err) {
      const error = normalizeError(err);

      logger.error(error);
    }
  };

  const redirectToChat = async () => {
    if (!selectedExpert) {
      redirectToExpertsPage();
      return;
    }

    try {
      const { chatId } = await chatService.createChat(selectedExpert.id);

      const commandParams = stringifyQueryParams({
        chatId,
        text: outgoingMessage,
      });
      await createCommand(`sendMessage?${commandParams}`);
    } catch (err) {
      const error = normalizeError(err);

      logger.error(error);
      redirectToExpertsPage();
    }
  };

  const skip = async () => {
    analyticsService.smsInputScreenClicked({ eventValue: 'closed' });

    await redirectToChat();
  };

  return (
    <>
      {renderQuizScreen({
        totalSteps,
        stepOrder,
        onSubmit,
        userCountry,
        skip,
        ...(stepData as PhoneNumberStepData),
      })}
    </>
  );
};

export default PhoneNumberWithRedirectQuizStep;
